'use client';

import { CSSProperties, useEffect, useRef, useState } from 'react';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import styles from './ImageItemListSection.module.css';

export type ImageItemListItem = {
  /** Stable ID — used as the React key and the active selector. */
  id: string;
  title: string;
  /** 1–2 line copy revealed when the row is expanded. */
  description: string;
};

export type ImageItemOverlay = {
  /** Mono eyebrow above the title (e.g. "PROTOCOL 01"). */
  eyebrow: string;
  /** Glass-card title (e.g. "Inflammation"). */
  title: string;
  /** Body line under the title. */
  body: string;
  /** Position relative to the image, as percentages of the card. */
  top: string;
  left?: string;
  right?: string;
};

type ImageItemListSectionProps = {
  eyebrow: string;
  heading: string;
  description: string;
  items: ImageItemListItem[];
  /** Single fixed image used for the entire section — does not change
   *  with the selected item. */
  image: string;
  imageAlt: string;
  /** 'left' = image card on the left, copy on the right (Skin Health).
   *  'right' = image card on the right, copy on the left (Muscle Recovery). */
  mediaSide: 'left' | 'right';
  /** Optional glass cards rendered over the media image. Fade-in + rise
   *  on viewport entry with staggered delay between cards. */
  overlays?: ImageItemOverlay[];
};

/**
 * Photon — Skin Health / Muscle Recovery layout.
 * One fixed image per section. Click an item → its 1-2 line description
 * expands inline below the title. Selected row is full black, others
 * ~35% black. Pattern matches Figma 153:651.
 */
const ImageItemListSection = ({
  eyebrow,
  heading,
  description,
  items,
  image,
  imageAlt,
  mediaSide,
  overlays,
}: ImageItemListSectionProps) => {
  const [activeId, setActiveId] = useState(items[0]?.id ?? '');
  const sectionRef = useRef<HTMLElement | null>(null);
  /* Single revealed flag drives both the media-card entrance and any
     glass overlay fades. Flips true the first time the section enters
     the viewport (~25% threshold) and never reverts. */
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setRevealed(true);
          io.disconnect();
        }
      },
      { threshold: 0.25 },
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  const renderOverlays = (key: 'desktop' | 'mobile') =>
    overlays?.map((o, i) => (
      <div
        key={`${key}-${i}`}
        className={styles.glassCard}
        data-visible={revealed}
        style={
          {
            top: o.top,
            left: o.left,
            right: o.right,
            ['--glass-delay']: `${i * 140}ms`,
          } as CSSProperties
        }
      >
        <span className={styles.glassEyebrow}>{o.eyebrow}</span>
        <span className={styles.glassTitle}>{o.title}</span>
        <span className={styles.glassBody}>{o.body}</span>
      </div>
    ));

  return (
    <section className={styles.section} ref={sectionRef}>
      <div
        className={styles.inner}
        style={
          {
            ['--inner-flex']: mediaSide === 'right' ? 'row-reverse' : 'row',
          } as CSSProperties
        }
      >
        <div className={styles.mediaColumn}>
          <div className={styles.mediaCard} data-revealed={revealed}>
            <CustomImage
              className={styles.mediaImage}
              src={image}
              alt={imageAlt}
              fill
              sizes="(max-width: 768px) 100vw, 600px"
            />
            {overlays && renderOverlays('desktop')}
          </div>
        </div>

        <div className={styles.contentColumn}>
          <div className={styles.header}>
            <p className={styles.eyebrow}>{eyebrow}</p>
            <h2 className={styles.heading}>{heading}</h2>
            <p className={styles.description}>{description}</p>
          </div>

          <div className={styles.mobileMediaCard} data-revealed={revealed}>
            <CustomImage
              className={styles.mobileMediaImage}
              src={image}
              alt={imageAlt}
              fill
              sizes="100vw"
            />
            {overlays && renderOverlays('mobile')}
          </div>

          <div className={styles.itemList} role="list">
            {items.map((item) => {
              const isActive = item.id === activeId;
              return (
                <div className={styles.item} key={item.id} role="listitem">
                  <button
                    className={styles.itemButton}
                    type="button"
                    aria-pressed={isActive}
                    aria-expanded={isActive}
                    aria-controls={`item-desc-${item.id}`}
                    data-active={isActive}
                    onClick={() => setActiveId(item.id)}
                  >
                    <span className={styles.itemTitle}>{item.title}</span>
                    <span
                      className={styles.plusIcon}
                      aria-hidden="true"
                      data-active={isActive}
                    >
                      <span />
                      <span />
                    </span>
                  </button>
                  {isActive && (
                    <p
                      className={styles.itemDescription}
                      id={`item-desc-${item.id}`}
                    >
                      {item.description}
                    </p>
                  )}
                  <div className={styles.itemSeparator} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImageItemListSection;
